import React, { useEffect, useRef, useState } from 'react'
// types
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset,
  IFileAsset
} from '../../types'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'

// custom hooks
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'

// functions
import { assetDialogEvent } from '../../utils/assetDialogEvent'
import { getAssetType } from '../../utils/getAssetType'
import { getBigAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'

// components
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'

// styles
import style from './AssetsModal.module.scss'

const ZOOM_STEP = 0.5
const ZOOM_MAX = 2
const PAN_LIMIT = 240

interface IAssetsModalProps {
  answer: IEcommerceAnswerSchema
  assetsCollection: assetsCollectionElement[]
  index: number
  onClose: () => void
}

const AssetsModal = ({ answer, assetsCollection, index, onClose }: IAssetsModalProps) => {
  const { t } = useSurveyLocale()
  const timeline = useTimeline()
  const viewport = useViewport()

  // state
  const [selectedIndex, setSelectedIndex] = useState<number>(index)
  const [zoom, setZoom] = useState<number>(1)
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [dragStart, setDragStart] = useState<{ x: number; y: number; posX: number; posY: number } | null>(null)

  // refs
  const playingIndexRef = useRef<number | null>(null)
  const zoomedIndexRef = useRef<number | null>(null)

  // variables
  const currentAsset = assetsCollection[selectedIndex]
  const currentType = getAssetType(currentAsset)

  // functions
  const logVideoEvent = (type: 'play_video' | 'pause_video', assetIndex: number) =>
    timeline.log({ type, ref: answer.id, target: assetIndex.toString(), context: ASSETS_TYPES.VIDEO.toLowerCase() })

  // The <video> unmounts on asset switch and on close, so its own pause event never reaches React.
  const flushPlayingVideo = () => {
    if (null === playingIndexRef.current) return
    logVideoEvent('pause_video', playingIndexRef.current)
    playingIndexRef.current = null
  }

  // Asset switch and unmount reset zoom without a minus click; flush one closing out per zoomed image, whatever the depth.
  const flushZoom = () => {
    if (null === zoomedIndexRef.current) return
    timeline.log({ type: 'zoom_image_out', ref: answer.id, target: zoomedIndexRef.current.toString() })
    zoomedIndexRef.current = null
  }

  const onZoomIn = () => {
    if (zoom >= ZOOM_MAX) return
    timeline.log({ type: 'zoom_image_in', ref: answer.id, target: selectedIndex.toString() })
    zoomedIndexRef.current = selectedIndex
    setZoom(value => Math.min(value + ZOOM_STEP, ZOOM_MAX))
  }

  const onZoomOut = () => {
    if (1 === zoom) return
    timeline.log({ type: 'zoom_image_out', ref: answer.id, target: selectedIndex.toString() })
    if (zoom - ZOOM_STEP <= 1) zoomedIndexRef.current = null
    setZoom(value => {
      const next = Math.max(value - ZOOM_STEP, 1)
      if (1 === next) setPosition({ x: 0, y: 0 })
      return next
    })
  }

  const onDragStart = (point: { clientX: number; clientY: number }) => {
    if (zoom <= 1) return
    setIsDragging(true)
    setDragStart({ x: point.clientX, y: point.clientY, posX: position.x, posY: position.y })
  }

  const onDragMove = (point: { clientX: number; clientY: number }) => {
    if (!isDragging || !dragStart) return
    const limit = (zoom - 1) * PAN_LIMIT
    setPosition({
      x: Math.max(-limit, Math.min(limit, dragStart.posX + point.clientX - dragStart.x)),
      y: Math.max(-limit, Math.min(limit, dragStart.posY + point.clientY - dragStart.y))
    })
  }

  const onDragEnd = () => {
    setIsDragging(false)
    setDragStart(null)
  }

  const onAssetSelect = (assetIndex: number) => {
    if (assetIndex === selectedIndex) return // same-thumb click: nothing changes, a flush here would orphan the playing video
    flushPlayingVideo()
    flushZoom()
    // Crossing a type boundary re-frames the dialog: close the outgoing type, open the incoming one
    // (same semantics as a tab switch in the Amazon / Walmart viewers). Same-type moves emit only show_asset.
    const next = assetsCollection[assetIndex]
    if (getAssetType(currentAsset) !== getAssetType(next)) {
      timeline.log({ ref: answer.id, type: assetDialogEvent('close', currentAsset) })
      timeline.log({ ref: answer.id, type: assetDialogEvent('open', next) })
    }
    setSelectedIndex(assetIndex)
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const closeModal = () => {
    flushPlayingVideo()
    flushZoom()
    timeline.log({ ref: answer.id, type: assetDialogEvent('close', currentAsset) })
    onClose()
  }

  // effects
  useEffect(() => {
    timeline.log({
      type: 'show_asset',
      ref: answer.id,
      target: selectedIndex.toString(),
      context: (getAssetType(assetsCollection[selectedIndex]) ?? '').toLowerCase()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex])

  // No deps: closeModal closes over the current asset, so the handler must be re-bound each render.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ('Escape' === event.key) closeModal()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  })

  useEffect(
    () => () => {
      flushPlayingVideo()
      flushZoom()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // render
  return (
    <div
      className={`${style.overlay} ${viewport.mobile ? style.overlayMobile : ''}`}
      data-testid="SW-EC-AssetsModal"
      onClick={closeModal}
    >
      <div className={style.rail} onClick={event => event.stopPropagation()}>
        {assetsCollection.map((asset, assetIndex) => (
          <button
            key={assetIndex}
            className={`${style.thumb} ${assetIndex === selectedIndex ? style.thumbActive : ''}`}
            onClick={() => onAssetSelect(assetIndex)}
          >
            {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
              <div
                className={style.thumbAsset}
                role="img"
                aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
                style={{ backgroundImage: `url(${getSmallAssetPath(asset as IImageAsset)})` }}
              />
            )}
            {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
              <div
                className={style.thumbAsset}
                role="presentation"
                style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
              />
            )}
            {ASSETS_TYPES.MODEL === getAssetType(asset) && (
              <div className={style.thumbModel}>
                <model-viewer src={(asset as IFileAsset).file_url} camera-controls={false} interaction-prompt="none" />
                <div className={style.layout} />
              </div>
            )}
          </button>
        ))}
      </div>
      <div className={style.stage} onClick={event => event.stopPropagation()}>
        {ASSETS_TYPES.IMAGE === currentType && (
          <div
            className={style.imageViewport}
            onMouseDown={event => onDragStart(event)}
            onMouseMove={event => onDragMove(event)}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            onTouchStart={event => onDragStart(event.touches[0])}
            onTouchMove={event => onDragMove(event.touches[0])}
            onTouchEnd={onDragEnd}
          >
            <div
              className={style.image}
              role="img"
              aria-label={(currentAsset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
              style={{
                backgroundImage: `url(${getBigAssetPath(currentAsset as IImageAsset)})`,
                backgroundPosition: `calc(50% + ${position.x}px) calc(50% + ${position.y}px)`,
                transform: `scale(${zoom})`,
                cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
            />
          </div>
        )}
        {ASSETS_TYPES.VIDEO === currentType && (
          <video
            key={selectedIndex} // remount on video->video switch; a reused node can fire the browser's src-change pause into the new index
            playsInline
            controls
            className={style.video}
            src={(currentAsset as IEcommerceVideoAsset).video.url}
            preload="metadata"
            onPlay={() => {
              playingIndexRef.current = selectedIndex
              logVideoEvent('play_video', selectedIndex)
            }}
            onPause={() => {
              playingIndexRef.current = null
              logVideoEvent('pause_video', selectedIndex)
            }}
          />
        )}
        {ASSETS_TYPES.MODEL === currentType && (
          <div className={style.model}>
            <model-viewer
              src={(currentAsset as IFileAsset).file_url}
              camera-controls={viewport.mobile}
              interaction-prompt="none"
            />
            {!viewport.mobile && <div className={style.layout} />}
          </div>
        )}
        {selectedIndex > 0 && !viewport.mobile && (
          <button
            className={`${style.control} ${style.prev}`}
            onClick={() => onAssetSelect(selectedIndex - 1)}
            aria-label={t('questions', 'ec', 'costco', 'previous')}
          >
            <i className="pi pi-chevron-left" />
          </button>
        )}
        {selectedIndex < assetsCollection.length - 1 && !viewport.mobile && (
          <button
            className={`${style.control} ${style.next}`}
            onClick={() => onAssetSelect(selectedIndex + 1)}
            aria-label={t('questions', 'ec', 'costco', 'next')}
          >
            <i className="pi pi-chevron-right" />
          </button>
        )}
        {ASSETS_TYPES.IMAGE === currentType && !viewport.mobile && (
          <div className={style.zoomControls}>
            <button
              className={style.control}
              onClick={onZoomIn}
              disabled={zoom >= ZOOM_MAX}
              aria-label={t('questions', 'ec', 'costco', 'zoomIn')}
            >
              <i className="pi pi-plus" />
            </button>
            <button
              className={style.control}
              onClick={onZoomOut}
              disabled={1 === zoom}
              aria-label={t('questions', 'ec', 'costco', 'zoomOut')}
            >
              <i className="pi pi-minus" />
            </button>
          </div>
        )}
      </div>
      <button
        className={`${style.control} ${style.close}`}
        onClick={event => {
          event.stopPropagation() // direct overlay child; the overlay's own onClick would close twice
          closeModal()
        }}
        aria-label={t('questions', 'ec', 'costco', 'close')}
      >
        <i className="pi pi-times" />
      </button>
    </div>
  )
}

export default AssetsModal
